import { FC, useContext, useEffect } from 'react'
import { Box, Flex } from '@/components/atoms/Grid'
import { H3, Body, InformationH2 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import { validFor } from '@/theme/validFor'
import styled from 'styled-components'
import InformationBadge from '@/components/molecules/informationBadge'
import { aboutImages } from '@/core/aboutImages'
import PageContentWrapper, {
  PageContentWrapperBackgroundStyle
} from '@/components/organisms/pageContentWrapper'
import { InformationSection } from '@/components/atoms/informationSection'
import { StoreContext, useDomain } from '@/providers/storeProvider'
import { Page } from '@/network/graphql.g'
import { requestThrow401 } from '@/utils/graphQl'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import { getMainData } from '@/utils/mainDataHelper'
import { SeoTechnicalPage } from '@/utils/seoData'
import SimpleImageBrowser from '@/components/organisms/imagesBrowser/simpleImageBrowser'

const About: FC = () => {
  const { t } = useTranslation('common')
  const { domainName } = useDomain()
  const { isFrogies } = useContext(StoreContext)

  const informationBadges = [
    {
      id: 1,
      image: '/static/aboutus/icon-return.png',
      title: t('Information.About.advantagesReturns'),
      info: t('Information.About.advantagesReturnsInfo')
    },
    {
      id: 2,
      image: '/static/aboutus/icon-money-back.png',
      title: t('Information.About.advantagesRefund'),
      info: t('Information.About.advantagesRefundInfo')
    },
    {
      id: 3,
      image: '/static/aboutus/icon-original.png',
      title: t('Information.About.advantagesOriginal'),
      info: t('Information.About.advantagesOriginalInfo')
    }
  ]

  const frogiesMarketText = [
    { id: 0, text: t('Information.About.Frogies.textP1') },
    { id: 1, text: t('Information.About.Frogies.textP2') },
    { id: 2, text: t('Information.About.Frogies.textP3') },
    { id: 3, text: t('Information.About.Frogies.textP4') }
  ]

  const restMarketsText = [
    { id: 0, text: t('Information.About.textP1') },
    { id: 1, text: t('Information.About.textP2') }
  ]

  const { pushAboutPage } = useMhub()
  useEffect(() => {
    pushAboutPage()
  }, [])

  return (
    <>
      <SeoTechnicalPage typePage="About"></SeoTechnicalPage>
      <PageContentWrapper
        mb={96}
        backgroundStyle={PageContentWrapperBackgroundStyle.normal}
      >
        <InformationH2 mb={0}>{t('Information.About.headline')}</InformationH2>
        <InformationSection>
          <Flex
            flexDirection={{ desktop: 'row', tablet: 'row', mobile: 'column' }}
          >
            <Box
              width={{ desktop: '100%', tablet: '100%' }}
              pr={{ desktop: 2, tablet: 2 }}
            >
              {isFrogies ? (
                <Box>
                  {frogiesMarketText.slice(0, 2).map((item) => (
                    <Body key={item.id}>{item.text}</Body>
                  ))}
                </Box>
              ) : (
                <Box>
                  {restMarketsText.map((item) => (
                    <Body key={item.id}>{item.text}</Body>
                  ))}
                </Box>
              )}
            </Box>
          </Flex>
          {isFrogies ? (
            <Box>
              {frogiesMarketText.slice(-2).map((item) => (
                <Body key={item.id}>{item.text}</Body>
              ))}
            </Box>
          ) : (
            <Box>
              <Body mt={0}>{t('Information.About.textP3')}</Body>
            </Box>
          )}
        </InformationSection>
        <InformationSection>
          <StyledH3>
            {t('Information.About.advantagesHeadline', {
              subdomain: t(`Information.About.textP4${domainName}`)
            })}
          </StyledH3>
          <Flex justifyContent="space-between" flexWrap="wrap">
            {informationBadges.map((informationBadge) => (
              <InformationBadge
                key={informationBadge.id}
                image={informationBadge.image}
                title={informationBadge.title}
                info={informationBadge.info}
              />
            ))}
          </Flex>
        </InformationSection>
        <InformationSection>
          <StyledH3>{t('Information.About.advantagesSliderHeadline')}</StyledH3>
          {aboutImages.map((tab) => (
            <SimpleImageBrowser imageUrls={tab.images} />
          ))}
        </InformationSection>
      </PageContentWrapper>
    </>
  )
}

export default About

export const getServerSideProps = withBaseServerSideProps(
  async ({
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<Rest>> => {
    const { response } = await requestThrow401({
      method: sdk.getSeoTags,
      variables: {
        page: Page.About,
        withMainNavigation,
        withStore
      }
    })

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags
      }
    }
  }
)

const StyledH3 = styled(H3)`
  text-align: center;
  text-transform: uppercase;
  ${validFor.tablet`
    font-size: 16px;
  `}
  ${validFor.mobile`
    font-size: 16px;
  `}
`
